import { useEffect, useState } from "react";

export default function ClassExamPage({
  openExamSessions = [],
  loadingOpenExams,
  fetchOpenExamSessions,
  examQuestions = [],
  loadingExamQuestions,
  fetchExamQuestions,
  currentAttemptId,
  submittingExam,
  startExamAttempt,
  submitExamAttempt,
  viewReportAnswers = [],
  loadingViewReport,
  fetchAttemptAnswers,
  goBackToHome,
  styles,
  studentNotes = [],
  newStudentNote,
  setNewStudentNote,
  loadingStudentNotes,
  fetchStudentNotes,
  addStudentNote,
  updateStudentNote,
  deleteStudentNote,
}) {
  const [selectedExamSession, setSelectedExamSession] = useState(null);
  const [examStarted, setExamStarted] = useState(false);
  const [showReviewPage, setShowReviewPage] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [viewOnlyReport, setViewOnlyReport] = useState(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [markedQuestions, setMarkedQuestions] = useState({});
  const [timeLeft, setTimeLeft] = useState(120 * 60);

  const questions = examQuestions;
  const currentQuestion = questions[currentIndex];
  const selectedAnswer = answers[currentQuestion?.id];

  const [selectedReportQuestion, setSelectedReportQuestion] = useState(null);
  const [showStudentQuestionModal, setShowStudentQuestionModal] = useState(false);
  const [editingStudentNoteId, setEditingStudentNoteId] = useState(null);
  const [editingStudentNoteText, setEditingStudentNoteText] = useState("");
  const [submittedAnswersSnapshot, setSubmittedAnswersSnapshot] = useState(null);

  useEffect(() => {
    if (!examStarted || submitted) return;

    if (timeLeft <= 0) {
      setSubmitted(true);
      setShowReviewPage(false);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [examStarted, submitted, timeLeft]);

  const startExam = async (exam) => {
    setSelectedExamSession(exam);
    setExamStarted(false);
    setSubmitted(false);
    setShowReviewPage(false);
    setCurrentIndex(0);
    setAnswers({});
    setMarkedQuestions({});
    setSubmittedAnswersSnapshot(null);
    setViewOnlyReport(null);
    setTimeLeft((exam.duration_minutes || 120) * 60);

    const loadedQuestions = await fetchExamQuestions(exam.exam_set_id);

    if (!loadedQuestions || loadedQuestions.length === 0) {
        alert("No questions found for this exam set.");
        return;
    }

    const attemptId = await startExamAttempt(
      exam.id,
      loadedQuestions.length,
      exam.activeRetakePermission?.id || null
    );

    if (!attemptId) {
        return;
    }

    setExamStarted(true);
  };

  const viewExistingReport = async (exam) => {
    setSelectedExamSession(exam);
    setViewOnlyReport(exam.submittedAttempt);

    await fetchExamQuestions(exam.exam_set_id);
    await fetchAttemptAnswers(exam.submittedAttempt.id);

    setSubmitted(true);
    setExamStarted(false);
    setShowReviewPage(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const handleAnswerSelect = (questionId, optionIndex) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: optionIndex,
    }));
  };

  const toggleMarkForReview = () => {
    setMarkedQuestions((prev) => ({
      ...prev,
      [currentQuestion.id]: !prev[currentQuestion.id],
    }));
  };

  const goToQuestion = (index) => {
    setCurrentIndex(index);
    setShowReviewPage(false);
  };

  const handleSubmitExam = async () => {
    const confirmSubmit = window.confirm(
        "Are you sure you want to submit this exam?"
    );

    if (!confirmSubmit) return;

    const success = await submitExamAttempt({
      attemptId: currentAttemptId,
      questions,
      answers,
      retakePermissionId: selectedExamSession?.activeRetakePermission?.id || null,
    });

    if (!success) return;

    const snapshotByQuestionNumber = Object.fromEntries(
      questions.map((q) => [
        q.question_number || q.id,
        answers[q.id] === undefined ? null : answers[q.id],
      ])
    );

    setSubmittedAnswersSnapshot(snapshotByQuestionNumber);

    await fetchOpenExamSessions();

    setViewOnlyReport(null);
    setSubmitted(true);
    setShowReviewPage(false);
  };

  const reportAnswerMap =
    submittedAnswersSnapshot
      ? submittedAnswersSnapshot
      : viewOnlyReport && viewReportAnswers.length > 0
      ? Object.fromEntries(
          viewReportAnswers.map((a) => [a.question_id, a.selected_answer])
        )
      : answers;

  const correctCount = questions.filter(
    (q) => reportAnswerMap[q.question_number || q.id] === q.correct_answer
  ).length;
      
  if (submitted) {
    const answeredCount = questions.filter(
      (q) => reportAnswerMap[q.question_number || q.id] !== undefined &&
            reportAnswerMap[q.question_number || q.id] !== null
    ).length;

    const markedCount = Object.values(markedQuestions).filter(Boolean).length;

    const incorrectQuestions = questions.filter((q) => {
      const key = q.question_number || q.id;
      const selected = reportAnswerMap[key];

      return selected !== undefined && selected !== null && selected !== q.correct_answer;
    });

    const notAnsweredQuestions = questions.filter((q) => {
      const key = q.question_number || q.id;
      const selected = reportAnswerMap[key];

      return selected === undefined || selected === null;
    });

    const domainSummaryMap = {};

    questions.forEach((q) => {
      const key = q.question_number || q.id;
      const selected = reportAnswerMap[key];

      const domain = q.domain || q.topic || "Uncategorized";

      if (!domainSummaryMap[domain]) {
        domainSummaryMap[domain] = {
          domain,
          total: 0,
          correct: 0,
          incorrect: 0,
          notAnswered: 0,
          incorrectQuestions: [],
          notAnsweredQuestions: [],
        };
      }

      domainSummaryMap[domain].total += 1;

      if (selected === undefined || selected === null) {
        domainSummaryMap[domain].notAnswered += 1;
        domainSummaryMap[domain].notAnsweredQuestions.push(q);
      } else if (selected === q.correct_answer) {
        domainSummaryMap[domain].correct += 1;
      } else {
        domainSummaryMap[domain].incorrect += 1;
        domainSummaryMap[domain].incorrectQuestions.push(q);
      }
    });

    const domainSummary = Object.values(domainSummaryMap)
      .map((d) => ({
        ...d,
        accuracy:
          d.total > 0 ? Math.round((d.correct / d.total) * 100) : 0,
      }))
      .sort((a, b) => b.incorrect + b.notAnswered - (a.incorrect + a.notAnswered));

    return (
      <div style={styles.card}>
        <div style={styles.topBar}>
          <div>
            <h2 style={styles.sectionTitle}>Exam Report</h2>
            <div style={styles.quizPath}>
              {selectedExamSession?.session_name || "Class Exam"}
            </div>
          </div>

          <button style={styles.secondaryButton} onClick={goBackToHome}>
            Back Home
          </button>
        </div>

        <div style={styles.reportGrid}>
          <div style={styles.reportCard}>
            <div style={styles.reportLabel}>Score</div>
            <div style={styles.reportValue}>
              {correctCount} / {questions.length}
            </div>
          </div>

          <div style={styles.reportCard}>
            <div style={styles.reportLabel}>Accuracy</div>
            <div style={styles.reportValue}>
              {Math.round((correctCount / questions.length) * 100)}%
            </div>
          </div>

          <div style={styles.reportCard}>
            <div style={styles.reportLabel}>Answered</div>
            <div style={styles.reportValue}>{answeredCount}</div>
          </div>

          <div style={styles.reportCard}>
            <div style={styles.reportLabel}>Marked</div>
            <div style={styles.reportValue}>{markedCount}</div>
          </div>
        </div>

        <div style={styles.emptyBox}>
          <h3 style={{ marginTop: 0 }}>Teacher Review Focus</h3>
          <div style={styles.emptyBox}>
            <h3 style={{ marginTop: 0 }}>Not Answered Summary</h3>

            {notAnsweredQuestions.length === 0 ? (
              <div>All questions were answered.</div>
            ) : (
              <div>
                You did not answer:{" "}
                <strong>
                  {notAnsweredQuestions
                    .map((q) => `Q${q.question_number || q.id}`)
                    .join(", ")}
                </strong>
              </div>
            )}
          </div>
          {incorrectQuestions.length === 0 ? (
            <div>Great job. No incorrect questions.</div>
          ) : (
            <div>
              These questions should be reviewed in class:{" "}
              <strong>
                {incorrectQuestions
                  .map((q) => `Q${questions.indexOf(q) + 1}`)
                  .join(", ")}
              </strong>
            </div>
          )}
        </div>

        <div style={{ marginTop: "24px" }}>
        <h3>Domain Performance</h3>

        <div style={styles.reportGrid}>
          {domainSummary.map((domain) => (
            <div key={domain.domain} style={styles.reportCard}>
              <div style={styles.reportLabel}>{domain.domain}</div>

              <div style={styles.reportValueSmall}>
                Accuracy: {domain.accuracy}%
              </div>

              <div style={{ marginTop: "8px", color: "#4b5563" }}>
                Correct: {domain.correct} / {domain.total}
              </div>

              <div style={{ marginTop: "6px", color: "#dc2626" }}>
                Incorrect:{" "}
                {domain.incorrectQuestions.length > 0
                  ? domain.incorrectQuestions
                      .map((q) => `Q${q.question_number || q.id}`)
                      .join(", ")
                  : "None"}
              </div>

              <div style={{ marginTop: "6px", color: "#92400e" }}>
                Not Answered:{" "}
                {domain.notAnsweredQuestions.length > 0
                  ? domain.notAnsweredQuestions
                      .map((q) => `Q${q.question_number || q.id}`)
                      .join(", ")
                  : "None"}
              </div>
            </div>
          ))}
        </div>
      </div>

        <div style={{ marginTop: "18px" }}>
          <h3>Question Review</h3>

          <div style={styles.reviewGrid}>
            {questions.map((question, index) => {
              const selected = reportAnswerMap[question.question_number || question.id];
              const isAnswered = selected !== undefined && selected !== null;
              const isCorrectAnswer = isAnswered && selected === question.correct_answer;
              const marked = markedQuestions[question.id];

              return (
                <div
                  key={question.id}
                  onClick={async () => {
                    const questionPayload = {
                      ...question,
                      selected_answer: selected,
                      is_answered: isAnswered,
                      is_correct: isCorrectAnswer,
                    };

                    setSelectedReportQuestion(questionPayload);

                    await fetchStudentNotes({
                      examQuestionId: question.id,
                      examSessionId: selectedExamSession.id,
                    });

                    setNewStudentNote("");
                    setEditingStudentNoteId(null);
                    setEditingStudentNoteText("");

                    setShowStudentQuestionModal(true);
                  }}
                  style={{
                    ...styles.resultQuestionCard,
                    ...(isCorrectAnswer ? styles.resultCorrect : styles.resultWrong),
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: "bold" }}>Q{index + 1}</div>

                  <div style={{ marginTop: "6px" }}>
                    {isAnswered
                      ? isCorrectAnswer
                        ? "Correct"
                        : "Incorrect"
                      : "Not Answered"}
                  </div>

                  {marked && (
                    <div style={{ marginTop: "6px", color: "#92400e" }}>
                      Marked for Review
                    </div>
                  )}

                  <div style={{ marginTop: "8px", fontSize: "13px" }}>
                    Your Answer:{" "}
                    {isAnswered ? String.fromCharCode(65 + selected) : "N/A"}
                  </div>

                  <div style={{ fontSize: "13px" }}>
                    Correct Answer:{" "}
                    {String.fromCharCode(65 + question.correct_answer)}
                  </div>
                </div>
              );
            })}
          </div>
          {showStudentQuestionModal && selectedReportQuestion && (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0,0,0,0.55)",
      zIndex: 9999,
      overflowY: "auto",
      padding: "40px",
      boxSizing: "border-box",
    }}
    onClick={() => {
      setShowStudentQuestionModal(false);
      setSelectedReportQuestion(null);
    }}
  >
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "20px",
        maxWidth: "900px",
        margin: "0 auto",
        padding: "30px",
        boxSizing: "border-box",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={styles.topBar}>
        <div>
          <h2 style={styles.sectionTitle}>
            Question {selectedReportQuestion.question_number}
          </h2>
          <div style={styles.quizPath}>
            Review your answer and the correct answer.
          </div>
        </div>

        <button
          style={styles.secondaryButton}
          onClick={() => {
            setShowStudentQuestionModal(false);
            setSelectedReportQuestion(null);
          }}
        >
          Close
        </button>
      </div>

      <p style={styles.questionText}>
        {selectedReportQuestion.question}
      </p>

      <div style={styles.optionsWrap}>
        {selectedReportQuestion.options.map((option, index) => {
          const isCorrect = index === selectedReportQuestion.correct_answer;
          const isStudentAnswer =
            selectedReportQuestion.is_answered &&
            index === selectedReportQuestion.selected_answer;

          return (
            <div
              key={index}
              style={{
                ...styles.optionButton,
                ...(isCorrect ? styles.correctOption : {}),
                ...(isStudentAnswer && !isCorrect
                  ? {
                      backgroundColor: "#fef2f2",
                      borderColor: "#dc2626",
                    }
                  : {}),
              }}
            >
              <span style={styles.optionLetter}>
                {String.fromCharCode(65 + index)}.
              </span>
              <span>{option}</span>

              {isCorrect && (
                <span style={{ marginLeft: "10px", fontWeight: "bold" }}>
                  Correct Answer
                </span>
              )}

              {isStudentAnswer && !isCorrect && (
                <span style={{ marginLeft: "10px", fontWeight: "bold" }}>
                  Your Answer
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: "20px" }}>
        <h3>Your Result</h3>
        <div style={{ marginTop: "24px" }}>
          <h3>My Notes</h3>

          <div style={styles.quizPath}>
            Your private notes for this question. Only you can see these notes.
          </div>

          {loadingStudentNotes ? (
            <div style={{ marginTop: "10px" }}>Loading notes...</div>
          ) : (
            <>
              {studentNotes.length === 0 ? (
                <div style={{ marginTop: "10px", color: "#6b7280" }}>
                  No notes yet.
                </div>
              ) : (
                <div style={{ marginTop: "12px" }}>
                  {studentNotes.map((note) => (
                    <div
                      key={note.id}
                      style={{
                        padding: "12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "10px",
                        marginBottom: "10px",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      {editingStudentNoteId === note.id ? (
                        <>
                          <textarea
                            value={editingStudentNoteText}
                            onChange={(e) =>
                              setEditingStudentNoteText(e.target.value)
                            }
                            style={{
                              ...styles.loginInput,
                              minHeight: "80px",
                            }}
                          />

                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              marginTop: "8px",
                            }}
                          >
                            <button
                              style={styles.smallPrimaryButton}
                              onClick={async () => {
                                const success = await updateStudentNote(
                                  note.id,
                                  editingStudentNoteText
                                );

                                if (success) {
                                  setEditingStudentNoteId(null);
                                  setEditingStudentNoteText("");

                                  await fetchStudentNotes({
                                    examQuestionId: selectedReportQuestion.id,
                                    examSessionId: selectedExamSession.id,
                                  });
                                }
                              }}
                            >
                              Save
                            </button>

                            <button
                              style={styles.smallSecondaryButton}
                              onClick={() => {
                                setEditingStudentNoteId(null);
                                setEditingStudentNoteText("");
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>{note.note_text}</div>

                          <div
                            style={{
                              marginTop: "6px",
                              fontSize: "12px",
                              color: "#6b7280",
                            }}
                          >
                            Updated: {note.updated_at}
                          </div>

                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              marginTop: "8px",
                            }}
                          >
                            <button
                              style={styles.smallSecondaryButton}
                              onClick={() => {
                                setEditingStudentNoteId(note.id);
                                setEditingStudentNoteText(note.note_text);
                              }}
                            >
                              Edit
                            </button>

                            <button
                              style={styles.smallSecondaryButton}
                              onClick={async () => {
                                const success = await deleteStudentNote(note.id);

                                if (success) {
                                  await fetchStudentNotes({
                                    examQuestionId: selectedReportQuestion.id,
                                    examSessionId: selectedExamSession.id,
                                  });
                                }
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <textarea
                placeholder="Add a new note for this question..."
                value={newStudentNote}
                onChange={(e) => setNewStudentNote(e.target.value)}
                style={{
                  ...styles.loginInput,
                  minHeight: "90px",
                  marginTop: "12px",
                }}
              />

              <button
                style={styles.smallPrimaryButton}
                onClick={async () => {
                  const success = await addStudentNote({
                    examQuestionId: selectedReportQuestion.id,
                    examSessionId: selectedExamSession.id,
                    noteText: newStudentNote,
                  });

                  if (success) {
                    setNewStudentNote("");

                    await fetchStudentNotes({
                      examQuestionId: selectedReportQuestion.id,
                      examSessionId: selectedExamSession.id,
                    });
                  }
                }}
              >
                Add My Note
              </button>
            </>
          )}
        </div>
        <div>
          {selectedReportQuestion.is_answered
            ? selectedReportQuestion.is_correct
              ? "Correct"
              : "Incorrect"
            : "Not Answered"}
        </div>
      </div>
    </div>
  </div>
)}
        </div>
      </div>
    );
  }

  if (showReviewPage) {
    return (
      <div style={styles.card}>
        <div style={styles.topBar}>
          <div>
            <h2 style={styles.sectionTitle}>Review Exam</h2>
            <div style={styles.quizPath}>
              Review your answers before final submission.
            </div>
          </div>

          <div style={styles.emptyBox}>Timer: {formatTime(timeLeft)}</div>
        </div>

        <div style={styles.reviewGrid}>
          {questions.map((question, index) => {
            const answered = answers[question.id] !== undefined;
            const marked = markedQuestions[question.id];

            return (
              <button
                key={question.id}
                style={{
                  ...styles.reviewQuestionButton,
                  ...(answered ? styles.reviewAnswered : {}),
                  ...(marked ? styles.reviewMarked : {}),
                }}
                onClick={() => goToQuestion(index)}
              >
                <div>Q{index + 1}</div>
                <div style={{ fontSize: "12px", marginTop: "4px" }}>
                  {answered ? "Answered" : "Not Answered"}
                </div>
                {marked && (
                  <div style={{ fontSize: "12px", marginTop: "4px" }}>
                    Marked
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div style={styles.navButtons}>
          <button
            style={styles.secondaryButton}
            onClick={() => setShowReviewPage(false)}
          >
            Back to Questions
          </button>

          <button
            style={styles.primaryButton}
            onClick={handleSubmitExam}
            disabled={submittingExam}
            >
            {submittingExam ? "Submitting..." : "Submit Exam"}
          </button>
        </div>
      </div>
    );
  }
  if (loadingExamQuestions) {
    return (
        <div style={styles.card}>
        <div style={styles.emptyBox}>Loading exam questions...</div>
        </div>
    );
  }
  if (examStarted) {
    return (
      <div style={styles.card}>
        <div style={styles.topBar}>
          <div>
            <h2 style={styles.sectionTitle}>
              {selectedExamSession?.session_name || "Class Exam"}
            </h2>
            <div style={styles.quizPath}>
              Question {currentIndex + 1} of {questions.length}
            </div>
          </div>

          <div style={styles.emptyBox}>Timer: {formatTime(timeLeft)}</div>
        </div>

        <div style={styles.questionBox}>
          <p style={styles.questionText}>{currentQuestion.question}</p>
        </div>

        <div style={styles.optionsWrap}>
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              style={{
                ...styles.optionButton,
                ...(selectedAnswer === index ? styles.selectedOption : {}),
              }}
              onClick={() => handleAnswerSelect(currentQuestion.id, index)}
            >
              <span style={styles.optionLetter}>
                {String.fromCharCode(65 + index)}.
              </span>
              <span>{option}</span>
            </button>
          ))}
        </div>

        <div style={{ marginTop: "14px" }}>
          <button style={styles.secondaryButton} onClick={toggleMarkForReview}>
            {markedQuestions[currentQuestion.id]
              ? "Unmark Review"
              : "Mark for Review"}
          </button>
        </div>

        <div style={styles.navButtons}>
          <button
            style={styles.secondaryButton}
            disabled={currentIndex === 0}
            onClick={() => setCurrentIndex((prev) => prev - 1)}
          >
            Previous
          </button>

          <button
            style={styles.secondaryButton}
            onClick={() => setShowReviewPage(true)}
          >
            Review Summary
          </button>

          {currentIndex === questions.length - 1 ? (
            <button
              style={styles.primaryButton}
              onClick={() => setShowReviewPage(true)}
            >
              Next: Review
            </button>
          ) : (
            <button
              style={styles.primaryButton}
              onClick={() => setCurrentIndex((prev) => prev + 1)}
            >
              Next
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.topBar}>
        <div>
          <h2 style={styles.sectionTitle}>Class Exam</h2>
          <div style={styles.quizPath}>
            Join teacher-assigned mock exams and review your results.
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button style={styles.secondaryButton} onClick={fetchOpenExamSessions}>
            Refresh
          </button>

          <button style={styles.secondaryButton} onClick={goBackToHome}>
            Back Home
          </button>
        </div>
      </div>

      {loadingOpenExams ? (
        <div style={styles.emptyBox}>Loading class exams...</div>
      ) : openExamSessions.length === 0 ? (
        <div style={styles.emptyBox}>No active class exams yet.</div>
      ) : (
        <div style={styles.courseGrid}>
          {openExamSessions.map((exam) => (
            <div key={exam.id} style={styles.courseCard}>
              <div style={styles.courseBadge}>Open</div>

              <h3 style={styles.courseCardTitle}>{exam.session_name}</h3>

              <div style={styles.courseMeta}>
                <span>{exam.duration_minutes} minutes</span>
                <span>{exam.session_status}</span>
              </div>

              {exam.submittedAttempt && (
                <div style={{ marginTop: "10px", color: "#16a34a", fontWeight: "bold" }}>
                  Submitted · Score: {exam.submittedAttempt.score} /{" "}
                  {exam.submittedAttempt.total_questions} ·{" "}
                  {exam.submittedAttempt.accuracy_pct}%
                </div>
              )}

              <p style={styles.courseDescription}>
                Teacher-assigned class exam. Complete it before the session
                closes.
              </p>

              {exam.submittedAttempt && !exam.activeRetakePermission ? (
                <button
                  style={styles.courseButton}
                  onClick={() => viewExistingReport(exam)}
                >
                  View Report
                </button>
              ) : exam.submittedAttempt && exam.activeRetakePermission ? (
                <div style={{ display: "grid", gap: "10px" }}>
                  <button
                    style={styles.courseButton}
                    onClick={() => startExam(exam)}
                  >
                    Start Retake
                  </button>

                  <button
                    style={styles.secondaryButton}
                    onClick={() => viewExistingReport(exam)}
                  >
                    View Previous Report
                  </button>
                </div>
              ) : (
                <button
                  style={styles.courseButton}
                  onClick={() => startExam(exam)}
                >
                  Start Exam
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}